import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { rootStore } from '../../../mobx/rootStore';
import { TaskModel } from '../../../mobx/models/task';
import { TrackingActivityInterface } from '../../../interfaces/TimeTrackingInterface';
import UserPreferenceSingleton from '../../../helpers/userPreferenceSingleton';
import { nanoid } from 'nanoid';
import { getActiveTime, stopTracker, updateTimeLogDetails } from '../../../services/timeTrackingServices';
import { dispatchAppSuccess } from '../../../services/appService';
import { getTimelogData } from '../../../services/taskServices';
import { captureException } from '../../../services/logService';
import { ROUTES } from '../../../routes/urls';
import { MS_PER_HOUR } from '../../../global/constants';

interface UseTimeTrackingReturn {
  currentActiveTask: TaskModel | null | undefined;
  currentStartTime: { task_id: string | undefined; time: number };
  isLoading: boolean;
  onClickStartStop: (itemId: string, startingTime: string, item: TrackingActivityInterface) => void;
  onClickTaskDetails: () => void;
}

interface UseTimeTrackingParams {
  activeTimeLogs: TrackingActivityInterface[];
  tasks: TaskModel[];
}

export const useTimeTracking = ({ activeTimeLogs, tasks }: UseTimeTrackingParams): UseTimeTrackingReturn => {
  const [isLoading, setIsLoading] = useState(false);

  const { timeTrackingStore } = rootStore;
  const { currentStartTime } = timeTrackingStore;

  const history = useHistory();
  const params: { taskId: string } = useParams();
  const { taskId } = params;

  const currentActiveTask = useMemo(() => {
    if (!activeTimeLogs?.length) return null;

    return tasks.find((task) => task.id === activeTimeLogs[0]?.TaskId) || null;
  }, [activeTimeLogs, tasks]);

  const updateElapsedTime = useCallback(async () => {
    const startDate = new Date(activeTimeLogs[0]?.TimerStartedAt).getTime();
    const currentDate = new Date().getTime();
    const seconds = Math.max(0, Math.floor((currentDate - startDate) / 1000));
    timeTrackingStore.setCurrentStartTime({ task_id: activeTimeLogs[0]?.TaskId, time: seconds });
  }, [timeTrackingStore, activeTimeLogs]);

  const updateElapsedTimeRef = useRef(updateElapsedTime);
  updateElapsedTimeRef.current = updateElapsedTime;

  useEffect(() => {
    if (!activeTimeLogs?.length) return;
    // Update right away so the timer isn't stuck for the first second.
    updateElapsedTimeRef.current();
    const id = setInterval(() => updateElapsedTimeRef.current(), 1000);
    return () => clearInterval(id);
  }, [activeTimeLogs?.length]);

  const onClickTaskDetails = useCallback(() => {
    history.push(
      ROUTES.TASK_DETAILS.replace(':id', currentActiveTask?.projectId || '').replace(
        ':taskId',
        currentActiveTask?.id || ''
      )
    );
  }, [history, currentActiveTask]);

  const onClickStartStop = useCallback(
    async (itemId: string, startingTime: string, item: TrackingActivityInterface) => {
      try {
        setIsLoading(true);
        if (!itemId) return;
        const date1 = new Date(startingTime).getTime();
        const date2 = new Date().getTime();
        const timeDifference = date2 - date1; // Difference in milliseconds
        const hoursDifference = timeDifference / MS_PER_HOUR;
        const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();

        // Use cached computed property from store
        const { historyWithoutTypename: historyUpdated, totalHours: historyHoursSum } =
          timeTrackingStore.processedActiveHistory;

        const payloadUpdate = {
          Hours: Number(Number(historyHoursSum || 0) + hoursDifference),
          TimerStartedAtUnset: true,
          History: [
            ...(historyUpdated || []),
            {
              _id: nanoid(),
              CreatedBy: userDetails?.id,
              CreatedTime: new Date().toISOString(),
              UpdatedBy: userDetails?.id,
              UpdatedTime: new Date().toISOString(),
              TimerStartedAt: startingTime,
              TimerEndedAt: new Date().toISOString(),
              Hours: hoursDifference
            }
          ]
        };
        const result = await updateTimeLogDetails(itemId, payloadUpdate);

        if (result) {
          stopTracker();
          dispatchAppSuccess('Time Stopped!');
          const currentProjectId = rootStore.projectStore.currentProject?.id;
          if (taskId && taskId === item?.TaskId && currentProjectId) {
            await getTimelogData(currentProjectId, taskId);
          }
        }
        await getActiveTime();
      } catch (e) {
        captureException(e);
      } finally {
        setIsLoading(false);
      }
    },
    [taskId, timeTrackingStore]
  );

  return {
    currentActiveTask,
    currentStartTime,
    isLoading,
    onClickStartStop,
    onClickTaskDetails
  };
};
